/**
 * MobileListView.js
 * 모바일 전용 학생/교사 명단 뷰
 * StudentListView에서 모바일 관련 코드를 분리하여 독립적으로 관리
 */

import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../firebase";
import { useModal } from "../../contexts/ModalContext";
import { isTeacherMember, shouldMaskTeacherInfoMobile } from "../../utils/roleUtils";
import { matchSearch } from "../../utils/chosung";
import MobileListScreen from "./MobileListScreen";
import MobileDetailView from "./MobileDetailView";
import MobileRegisterModal from "./MobileRegisterModal";
import { useMobileListHandlers } from "./useMobileListHandlers";
import {
  MOBILE_LIST_ICONS,
  buildDepartmentOptions,
  buildFilteredItems,
  getDeptColorClass,
} from "./mobileListUtils";
import { enrichRecord } from "../../utils/recordUtils";

const isPrimitiveComparable = (value) =>
  value == null || ["string", "number", "boolean"].includes(typeof value);

export default function MobileListView({
  students = [],
  teachers = [],
  visitLogs = [],
  attendanceLogs = [],
  listType = "student",
  setListType,
  searchTerm = "",
  setSearchTerm,
  onAdd,
  onUpdate,
  onDelete,
  onAddTeacher,
  onUpdateTeacher,
  onDeleteTeacher,
  onNavigateToVisit,
  onNavigateToAttendance,
  onUpdateLog,
  onDeleteLog,
  onAddLog,
  theme,
  selectedDepartment,
  myDepartments = [],
  userProfile,
  onOpenCheerModal,
  targetStudentId = null,
  onClearTargetStudentId,
  detailReturnMenu = null,
  onClearDetailReturnMenu,
  onNavigateMenu,
}) {
  const { openConfirm, openAlert } = useModal();
  const isTeacher = listType === "teacher";
  const isUserTeacher = isTeacherMember(userProfile);
  const safeStudents = Array.isArray(students) ? students : [];
  const safeTeachers = Array.isArray(teachers) ? teachers : [];

  const shouldMaskTeacherInfo = (teacherItem) =>
    shouldMaskTeacherInfoMobile({
      teacherItem,
      userProfile,
      isTeacherList: isTeacher,
      currentUserUid: auth.currentUser?.uid,
    });

  const resolveTargetStudent = () => {
    if (typeof targetStudentId === "string") {
      return students.find((student) => student.id === targetStudentId) || null;
    }
    if (targetStudentId?.kind === "studentName" && targetStudentId.value) {
      return students.find((student) => student.name === targetStudentId.value) || null;
    }
    return null;
  };

  const [viewingItem, setViewingItem] = useState(() => {
    if (isTeacher || !targetStudentId) return null;
    return resolveTargetStudent();
  });
  const [isDeepLinkedDetailOpen, setIsDeepLinkedDetailOpen] = useState(Boolean(targetStudentId));
  const [isDetailEditing, setIsDetailEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [viewingLog, setViewingLog] = useState(null);
  const [sortType, setSortType] = useState(isTeacher ? "name" : "grade");
  const [sortOrder, setSortOrder] = useState("asc");
  const [faithFilter, setFaithFilter] = useState("all");
  const [attendanceFilter, setAttendanceFilter] = useState("all"); // 'all', '2weeks', '4weeks'
  const [genderFilter, setGenderFilter] = useState("all");

  const [isCreatingLog, setIsCreatingLog] = useState(false);
  const [logInputContent, setLogInputContent] = useState("");
  const [logInputType, setLogInputType] = useState("면담");
  const [logInputDate, setLogInputDate] = useState(new Date().toISOString().split("T")[0]);
  const [logInputSentiment, setLogInputSentiment] = useState("neutral");
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    gender: "남",
    phone: "",
    grade: "",
    class: "",
    school: "",
    guardian1Name: "",
    guardian1Phone: "",
    guardian2Name: "",
    guardian2Phone: "",
    birth: "",
    address: "",
    faithState: "기존신자",
    notes: "",
    photoUrl: "",
    prayerTopic: "",
  });

  const [teacherFormData, setTeacherFormData] = useState({
    name: "",
    gender: "남",
    phone: "",
    grade: "",
    birth: "",
    address: "",
    notes: "",
    role: "교사",
    charge: "",
    photoUrl: "",
  });

  const [isRegisterDeptOpen, setIsRegisterDeptOpen] = useState(false);
  const [isDetailDeptOpen, setIsDetailDeptOpen] = useState(false);
  useEffect(() => {
    if (isTeacher) {
      if (sortType === "class" || sortType === "grade") {
        setSortType("name");
      }
    } else if (sortType === "responsibility" || sortType === "role" || sortType === "department") {
      setSortType("grade");
    }
  }, [isTeacher, sortType]);

  useEffect(() => {
    if (!viewingItem) return;

    const collection = isTeacher ? safeTeachers : safeStudents;
    const refreshed = collection.find(c => c.id === viewingItem.id);
    if (refreshed) {
      setViewingItem(enrichRecord(refreshed, attendanceLogs, visitLogs));
    }
  }, [attendanceLogs, visitLogs, safeStudents, safeTeachers, isTeacher, viewingItem?.id]);

  useEffect(() => {
    if (!targetStudentId || isTeacher) return;

    const targetStudent = resolveTargetStudent();
    if (!targetStudent) return;

    setViewingItem((prev) => (prev?.id === targetStudent.id ? prev : targetStudent));
    setIsDeepLinkedDetailOpen(true);
    if (onClearTargetStudentId) {
      onClearTargetStudentId();
    }
  }, [targetStudentId, isTeacher, students, onClearTargetStudentId]);

  const closeViewingItem = () => {
    setViewingItem(null);
    setIsDetailEditing(false);
    if (isDeepLinkedDetailOpen && detailReturnMenu && onNavigateMenu) {
      onNavigateMenu(detailReturnMenu);
      if (onClearDetailReturnMenu) onClearDetailReturnMenu();
    }
    setIsDeepLinkedDetailOpen(false);
  };

  useEffect(() => {
    if (!formData.birth || formData.birth.length < 4 || !isModalOpen) return;

    const matched = formData.birth.match(/^(\d{4})/);
    if (!matched) return;

    const birthYear = parseInt(matched[1], 10);
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear + 1;

    let suggestedGrade = "";
    if (age >= 20) suggestedGrade = "청년부";
    else if (age >= 17) suggestedGrade = "고등부";
    else if (age >= 14) suggestedGrade = "중등부";
    else if (age >= 11) suggestedGrade = "초등부";
    else if (age >= 8) suggestedGrade = "유년부";
    else if (age >= 6) suggestedGrade = "유치부";
    else suggestedGrade = "영아부";

    if (suggestedGrade && formData.grade !== suggestedGrade) {
      setFormData((prev) => ({ ...prev, grade: suggestedGrade }));
    }
  }, [formData.birth, formData.grade, isModalOpen]);


  const filteredItems = useMemo(
    () =>
      buildFilteredItems({
        isTeacher,
        students: safeStudents,
        teachers: safeTeachers,
        selectedDepartment,
        searchTerm,
        matchSearch,
        attendanceLogs,
        visitLogs,
        sortKey: sortType,
        sortOrder,
        faithFilter,
        attendanceFilter,
        genderFilter,
      }),
    [
      isTeacher,
      safeStudents,
      safeTeachers,
      selectedDepartment,
      searchTerm,
      attendanceLogs,
      visitLogs,
      sortType,
      sortOrder,
      faithFilter,
      attendanceFilter,
      genderFilter,
    ]
  );

  const departmentOptions = useMemo(
    () => buildDepartmentOptions({ myDepartments, userProfile }),
    [myDepartments, userProfile]
  );

  const handlers = useMobileListHandlers({
    isTeacher,
    userProfile,
    setListType,
    selectedDepartment,
    departmentOptions,
    attendanceLogs,
    visitLogs,
    viewingItem,
    setViewingItem,
    formData,
    setFormData,
    teacherFormData,
    setTeacherFormData,
    setIsDetailEditing,
    setIsModalOpen,
    setIsUploading,
    onAdd,
    onUpdate,
    onDelete,
    onAddTeacher,
    onUpdateTeacher,
    onDeleteTeacher,
    openAlert,
    openConfirm,
  });

  const resolveDeptColorClass = (deptName) => getDeptColorClass(deptName, userProfile);

  return (
    <>
      <MobileListScreen
        isTeacher={isTeacher}
        isUserTeacher={isUserTeacher}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortType={sortType}
        setSortType={setSortType}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        filteredItems={filteredItems}
        openDetail={(item) => {
          setIsDeepLinkedDetailOpen(false);
          handlers.openDetail(item);
        }}
        getDeptColorClass={resolveDeptColorClass}
        selectedDepartment={selectedDepartment}
        userProfile={userProfile}
        openRegisterModal={handlers.openRegisterModal}
        onTouchStart={handlers.onTouchStart}
        onTouchMove={handlers.onTouchMove}
        onListSwipeEnd={handlers.onListSwipeEnd}
        viewingLog={viewingLog}
        setViewingLog={setViewingLog}
        onUpdateLog={onUpdateLog}
        onDeleteLog={onDeleteLog}
        // New Filters
        faithFilter={faithFilter}
        setFaithFilter={setFaithFilter}
        attendanceFilter={attendanceFilter}
        setAttendanceFilter={setAttendanceFilter}
        genderFilter={genderFilter}
        setGenderFilter={setGenderFilter}
        theme={theme}
      />

      <MobileDetailView
        viewingItem={viewingItem}
        setViewingItem={setViewingItem}
        onCloseDetail={closeViewingItem}
        isTeacher={isTeacher}
        isDetailEditing={isDetailEditing}
        setIsDetailEditing={setIsDetailEditing}
        teacherFormData={teacherFormData}
        setTeacherFormData={setTeacherFormData}
        formData={formData}
        setFormData={setFormData}
        departmentOptions={departmentOptions}
        isDetailDeptOpen={isDetailDeptOpen}
        setIsDetailDeptOpen={setIsDetailDeptOpen}
        handleDetailSave={handlers.handleDetailSave}
        handleDelete={handlers.handleDelete}
        handleImageUpload={handlers.handleImageUpload}
        onTouchStart={handlers.onTouchStart}
        onTouchMove={handlers.onTouchMove}
        onModalSwipeEnd={handlers.onModalSwipeEnd}
        getDeptColorClass={resolveDeptColorClass}
        shouldMaskTeacherInfo={shouldMaskTeacherInfo}
        showLinkPopup={handlers.showLinkPopup}
        setShowLinkPopup={handlers.setShowLinkPopup}
        teamMembers={handlers.teamMembers}
        linkLoading={handlers.linkLoading}
        fetchTeamMembers={handlers.fetchTeamMembers}
        handleLinkAccount={handlers.handleLinkAccount}
        handleUnlinkAccount={handlers.handleUnlinkAccount}
        onOpenCheerModal={onOpenCheerModal}
        userProfile={userProfile}
        logInputType={logInputType}
        setLogInputType={setLogInputType}
        logInputContent={logInputContent}
        setLogInputContent={setLogInputContent}
        logInputDate={logInputDate}
        setLogInputDate={setLogInputDate}
        logInputSentiment={logInputSentiment}
        setLogInputSentiment={setLogInputSentiment}
        editingLogId={editingLogId}
        setEditingLogId={setEditingLogId}
        setIsCreatingLog={setIsCreatingLog}
        showAllLogs={showAllLogs}
        setShowAllLogs={setShowAllLogs}
        visitLogs={visitLogs}
        attendanceLogs={attendanceLogs}
        onUpdateLog={onUpdateLog}
        onDeleteLog={onDeleteLog}
        onAddLog={onAddLog}
        openAlert={openAlert}
        openConfirm={openConfirm}
        onNavigateToAttendance={onNavigateToAttendance}
        icons={MOBILE_LIST_ICONS}
      />

      <MobileRegisterModal
        isModalOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTouchStart={handlers.onTouchStart}
        onTouchMove={handlers.onTouchMove}
        onModalSwipeEnd={handlers.onModalSwipeEnd}
        isTeacher={isTeacher}
        formData={formData}
        setFormData={setFormData}
        teacherFormData={teacherFormData}
        setTeacherFormData={setTeacherFormData}
        handleImageUpload={handlers.handleImageUpload}
        handleSubmit={handlers.handleSubmit}
        departmentOptions={departmentOptions}
        isRegisterDeptOpen={isRegisterDeptOpen}
        setIsRegisterDeptOpen={setIsRegisterDeptOpen}
        icons={MOBILE_LIST_ICONS}
      />
    </>
  );
}
